import { signOut } from "@/lib/auth/auth.utils";
import Avatar, {
  AvatarFallback,
  AvatarImage,
  createAvatarFallback,
} from "@/lib/components/ui/avatar/avatar";
import Button from "@/lib/components/ui/button/button";
import Text from "@/lib/components/ui/text/text";
import ThemeProvider from "@/lib/theme/theme-provider";
import { ScopeCard } from "@/modules/assistants/components/scope-selection";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type OrgChooserTenant = {
  tenantId: string;
  name?: string;
  logoUrl?: string;
};

export type OrgChooserScreenProps = {
  tenants: OrgChooserTenant[];
  onChoose: (tenantId: string) => Promise<void>;
};

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * OrgChooserScreen is the first screen after login for users in more than one
 * company. It reuses the ScopeCard selection row (same one as the module
 * access-level screen): the user selects a company, then confirms with the
 * primary button (or double-clicks a row to do both); confirming switches the
 * Frontegg session into that tenant.
 */
export function OrgChooserScreen({ tenants, onChoose }: OrgChooserScreenProps) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const selectedTenant = tenants.find((tenant) => tenant.tenantId === selected);
  const selectedName = selectedTenant?.name ?? selectedTenant?.tenantId ?? "";

  function toggle(tenantId: string) {
    if (submitting) {
      return;
    }
    setFailed(false);
    setSelected((current) => (current === tenantId ? null : tenantId));
  }

  // select and enter in one go — the double-click shortcut for frequent switchers
  async function enter(tenantId: string) {
    if (submitting) {
      return;
    }
    setSelected(tenantId);
    setFailed(false);
    setSubmitting(true);
    try {
      await onChoose(tenantId);
      // on success the gate reloads the app into the new company
    } catch {
      setSubmitting(false);
      setFailed(true);
    }
  }

  return (
    <ThemeProvider>
      <div className="bg-background flex min-h-svh flex-col items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <Text variant="title" weight="semibold" className="text-center">
            {t("org_chooser_title")}
          </Text>
          <Text className="text-foreground-pressed mt-1 text-center">
            {t("org_chooser_subtitle")}
          </Text>

          {/* TODO: once the typical company count gets large (5+), add a search
              field above this list; the list already scrolls on its own */}
          <div
            role="group"
            aria-label={t("org_chooser_title")}
            className="mt-6 flex max-h-[60vh] flex-col gap-2.5 overflow-y-auto"
          >
            {tenants.map((tenant) => (
              // double-click on the row is the power-user "select + enter" shortcut;
              // it lives on the wrapper so ScopeCard stays untouched
              <div
                key={tenant.tenantId}
                onDoubleClick={() => enter(tenant.tenantId)}
              >
                <ScopeCard
                  selected={selected === tenant.tenantId}
                  onSelect={() => toggle(tenant.tenantId)}
                  title={tenant.name ?? tenant.tenantId}
                  description=""
                  icon={
                    <Avatar className="size-10 rounded-[11px]">
                      <AvatarFallback aria-hidden>
                        {createAvatarFallback(tenant.name)}
                      </AvatarFallback>
                      {tenant.logoUrl ? (
                        <AvatarImage src={tenant.logoUrl} alt="" />
                      ) : null}
                    </Avatar>
                  }
                />
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`mt-6 w-full disabled:pointer-events-none disabled:opacity-50 ${FOCUS_RING}`}
            disabled={selected === null || submitting}
            aria-busy={submitting}
            onClick={() => selected && enter(selected)}
          >
            {submitting
              ? t("org_chooser_open_loading", { name: selectedName })
              : selected
                ? t("org_chooser_open", { name: selectedName })
                : t("org_chooser_select")}
          </Button>

          {failed ? (
            <Text
              role="alert"
              className="text-foreground-critical mt-3 text-center text-sm"
            >
              {t("org_chooser_switch_error")}
            </Text>
          ) : null}

          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={FOCUS_RING}
              onClick={signOut}
            >
              {t("org_chooser_logout")}
            </Button>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
